import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Button,
  DataTable,
  Input,
  PageHeader,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Label,
  SpinnerLoader,
  Toastify,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { Edit, RefreshCw, Search } from 'lucide-react';
import { useSelector } from 'react-redux';
import { debounce } from '@lift-admin/utils/debounce';
import { useThrottledFn } from '@lift-admin/utils/throttle';
import apiClient from '@lift-admin/utils/apiClient';
import { buildSummary, diffFields } from '@lift-admin/utils/logChangeFormatter';
import { actionLog } from '../../../../App';
import axios from 'axios';
import { useAuthContext } from '@lift-admin/utils/provider';

const BrandWeights = () => {
  const user = JSON.parse(localStorage.getItem('user') as string);
  const { admin_auth, superAdmin } = useSelector((state: any) => state.admin);
  const { canEdit } = useAuthContext();
  const hasEditAccess = canEdit('affiliate_mgmt');

  const [brandWeights, setBrandWeights] = useState<any>([]);
  const [brandWeightsBackup, setBrandWeightsBackup] = useState<any>([]);
  const [searchText, setSearchText] = useState('');
  const [listLoad, setListLoad] = useState(true);
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);

  // Edit dialog states
  const [showEditBrandWeightDialog, setShowEditBrandWeightDialog] = useState(false);
  const [editBrandId, setEditBrandId] = useState<any>();
  const [editBrand, setEditBrand] = useState<any>();
  const [editRevenue, setEditRevenue] = useState<any>();
  const [editAOV, setEditAOV] = useState<any>();
  const [editClicks, setEditClicks] = useState<any>();
  const [editConvRate, setEditConvRate] = useState<any>();
  const [editNewVExisting, setEditNewVExisting] = useState<any>();
  const [editECPC, setEditECPC] = useState<any>();
  const [editConversions, setEditConversions] = useState<any>();
  const [editNewUser, setEditNewUser] = useState<any>();
  const [hideEditBrandWeightProgressDialog, setHideEditBrandWeightProgressDialog] = useState(true);

  const get_ref = useRef(true);
  useEffect(() => {
    if (get_ref.current) {
      get_ref.current = false;
      getBrandWeights();
    }
  }, []);

  const customSort = (a: any, b: any) => a.brand_id - b.brand_id;

  const getBrandWeights = () => {
    setListLoad(true);
    apiClient
      .get(`/brand/v1/admin/brand/weights`)
      .then((res: any) => {
        setListLoad(false);
        const sortedData = res.data.response.sort(customSort).reverse();
        console.log('Brand weights data loaded:', sortedData.length, 'items');
        setBrandWeights(sortedData);
        setBrandWeightsBackup(sortedData);
        setPageIndex(0); // Reset to first page when data loads
      })
      .catch((err: any) => {
        setListLoad(false);
        Toastify('error', err.response?.data?.message || 'Failed to fetch brand weights');
        console.error(err);
      });
  };

  const throttleGetBrandWeights = useThrottledFn(getBrandWeights, 1000);

  const totalWeight = (row: any) =>
    Math.round(
      (parseFloat(row.revenue) +
    parseFloat(row.aov) +
    parseFloat(row.click) +
    parseFloat(row.conv_rate) +
    parseFloat(row.new_v_existing) +
    parseFloat(row.ecpc) +
    parseFloat(row.conversions) +
        parseFloat(row.new_user)) *
        100
    ) / 100;

  const editBrandWeight = (row: any) => {
    setEditBrandId(row.brand_id);
    setEditBrand(row.brand.name);
    setEditRevenue(row.revenue);
    setEditAOV(row.aov);
    setEditClicks(row.click);
    setEditConvRate(row.conv_rate);
    setEditNewVExisting(row.new_v_existing);
    setEditECPC(row.ecpc);
    setEditConversions(row.conversions);
    setEditNewUser(row.new_user);
    setShowEditBrandWeightDialog(true);
  };

  const validateWeight = (value: any, fieldLabel: string): string | null => {
    if (value === undefined || value === null || value === '') {
      return `${fieldLabel} should be decimal and should not be empty.`;
    }
    const num = parseFloat(String(value).trim());
    if (Number.isNaN(num)) {
      return `${fieldLabel} should be a valid decimal.`;
    }
    if (num < 0 || num > 1) {
      return `${fieldLabel} must be between 0.0 and 1.0.`;
    }
    return null;
  };

  const changeDetected = () => {
    const brand: any = brandWeightsBackup.find(
      (b: any) => b.brand_id === editBrandId
    );

    const changes: any[] = [];
    if (brand) {
      if (brand.revenue != editRevenue) {
        changes.push(`${brand.revenue} => ${editRevenue} (revenue)`);
      }
      if (brand.aov != editAOV) {
        changes.push(`${brand.aov} => ${editAOV} (aov)`);
      }
      if (brand.click != editClicks) {
        changes.push(`${brand.click} => ${editClicks} (click)`);
      }
      if (brand.conv_rate != editConvRate) {
        changes.push(`${brand.conv_rate} => ${editConvRate} (conv_rate)`);
      }
      if (brand.new_v_existing != editNewVExisting) {
        changes.push(
          `${brand.new_v_existing} => ${editNewVExisting} (new_v_existing)`
        );
      }
      if (brand.ecpc != editECPC) {
        changes.push(`${brand.ecpc} => ${editECPC} (ecpc)`);
      }
      if (brand.conversions != editConversions) {
        changes.push(
          `${brand.conversions} => ${editConversions} (conversions)`
        );
      }
      if (brand.new_user != editNewUser) {
        changes.push(`${brand.new_user} => ${editNewUser} (new_user)`);
      }
    }

    return changes;
  };

  const updateBrandWeight = () => {
    const fields: { value: any; label: string }[] = [
      { value: editRevenue, label: 'Revenue Wt.' },
      { value: editAOV, label: 'AOV Wt.' },
      { value: editClicks, label: 'Clicks Wt.' },
      { value: editConvRate, label: 'Conversion Rate Wt.' },
      { value: editNewVExisting, label: 'New v/s Existing Wt.' },
      { value: editECPC, label: 'eCPC Wt.' },
      { value: editConversions, label: 'Conversions Wt.' },
      { value: editNewUser, label: 'New User %' },
    ];
    for (const { value, label } of fields) {
      const error = validateWeight(value, label);
      if (error) {
        Toastify('warning', error);
        return;
      }
    }

    setHideEditBrandWeightProgressDialog(false);

      let requestBody: any = {
        revenue: parseFloat(editRevenue),
        aov: parseFloat(editAOV),
        click: parseFloat(editClicks),
        conv_rate: parseFloat(editConvRate),
        new_v_existing: parseFloat(editNewVExisting),
        ecpc: parseFloat(editECPC),
        conversions: parseFloat(editConversions),
        new_user: parseFloat(editNewUser),
      };

      const oldWeights =
        brandWeightsBackup.find((b: any) => b.brand_id === editBrandId) || {};
      const weightChanges = diffFields(
        oldWeights,
        {
          revenue: requestBody.revenue,
          aov: requestBody.aov,
          click: requestBody.click,
          conv_rate: requestBody.conv_rate,
          new_v_existing: requestBody.new_v_existing,
          ecpc: requestBody.ecpc,
          conversions: requestBody.conversions,
          new_user: requestBody.new_user,
        },
        [
          { key: 'revenue', label: 'Revenue Wt.' },
          { key: 'aov', label: 'AOV Wt.' },
          { key: 'click', label: 'Clicks Wt.' },
          { key: 'conv_rate', label: 'Conversion Rate Wt.' },
          { key: 'new_v_existing', label: 'New v/s Existing Wt.' },
          { key: 'ecpc', label: 'eCPC Wt.' },
          { key: 'conversions', label: 'Conversions Wt.' },
          { key: 'new_user', label: 'New User %' },
        ]
      );

      if (superAdmin) {
        apiClient
          .put(`/brand/v1/admin/brand/weights/${editBrandId}`, requestBody)
          .then(async (res: any) => {
            Toastify('success', 'Brand Weight updated');
            setHideEditBrandWeightProgressDialog(true);
            setShowEditBrandWeightDialog(false);
            getBrandWeights();

            try {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              const desc = {
                message: buildSummary('Brand weight', editBrand, 'updated', weightChanges.length),
                category: 'brand',
                status: res.status,
                method: res.config?.method,
                url: res.config?.url,
                brand_id: editBrandId,
                brand_name: editBrand,
                user_id: user?.id,
                user_name: user?.name,
                changes: weightChanges,
              };
              await actionLog?.actionLogTrack(JSON.stringify(desc), false);
            } catch (logError) {
              console.error('Action log error:', logError);
            }
          })
          .catch(async (error: any) => {
            setHideEditBrandWeightProgressDialog(true);
            console.error(error);
            Toastify('error', error.response?.data?.message || 'Something went wrong!');

            try {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              const errorDesc = {
                message: `Failed to update brand weight for Brand (Brand ID: ${editBrandId}, Brand Name: "${editBrand}") by User (User ID: ${user?.id}, User Name: "${user?.name}"). Error: ${error.response?.data?.message || error.message}`,
                category: 'brand',
                status: error.response?.status,
                method: 'PUT',
                url: `/brand/v1/admin/brand/weights/${editBrandId}`,
                brand_id: editBrandId,
                brand_name: editBrand,
                user_id: user?.id,
                user_name: user?.name,
              };
              await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
            } catch (logError) {
              console.error('Action log error:', logError);
            }
          });
      } else {
        requestBody = {
          admin_id: admin_auth?.id,
          organization_id: admin_auth.organization,
          id: editBrandId,
          ...requestBody,
        };

        axios
          .post(
            `https://ltpautomatedpublisherscorecard.uc.r.appspot.com/public/admin/brand/weights/update`,
            requestBody,
            {
              headers: {
                Authorization: `JWT ${admin_auth.token}`,
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
            }
          )
          .then(async (res) => {
            Toastify('success', 'Brand Weight updated');
            setHideEditBrandWeightProgressDialog(true);
            setShowEditBrandWeightDialog(false);
            getBrandWeights();

            try {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              const desc = {
                message: buildSummary('Brand weight', editBrand, 'updated', weightChanges.length),
                category: 'brand',
                status: res.status,
                method: res.config?.method,
                url: res.config?.url,
                brand_id: editBrandId,
                brand_name: editBrand,
                user_id: user?.id,
                user_name: user?.name,
                changes: weightChanges,
              };
              await actionLog?.actionLogTrack(JSON.stringify(desc), false);
            } catch (logError) {
              console.error('Action log error:', logError);
            }
          })
          .catch(async (error) => {
            setHideEditBrandWeightProgressDialog(true);
            console.error(error);
            Toastify('error', error.response?.data?.message || 'Something went wrong!');

            try {
              const user = JSON.parse(localStorage.getItem('user') || '{}');
              const errorDesc = {
                message: `Failed to update brand weight for Brand (Brand ID: ${editBrandId}, Brand Name: "${editBrand}") by User (User ID: ${user?.id}, User Name: "${user?.name}"). Error: ${error.response?.data?.message || error.message}`,
                category: 'brand',
                status: error.response?.status,
                method: 'POST',
                url: `https://ltpautomatedpublisherscorecard.uc.r.appspot.com/public/admin/brand/weights/update`,
                brand_id: editBrandId,
                brand_name: editBrand,
                user_id: user?.id,
                user_name: user?.name,
              };
              await actionLog?.actionLogTrack(JSON.stringify(errorDesc), true);
            } catch (logError) {
              console.error('Action log error:', logError);
            }
          });
    }
  };

  // Search results
  const searchResults = (query: any) => {
    let results = [];
    for (let i = 0; i < brandWeightsBackup.length; i++) {
      if (
        brandWeightsBackup[i]['brand']?.name
          ?.toLowerCase()
          .includes(query.toLowerCase())
      )
        results.push(brandWeightsBackup[i]);
    }

    setBrandWeights(results);
  };

  const searchOptimized = useCallback(debounce(searchResults, 600), [
    brandWeightsBackup,
  ]);


  const admin_data = localStorage.getItem('admin_auth')
    ? JSON.parse(localStorage.getItem('admin_auth') || '')
    : null;

  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return brandWeights.slice(start, start + pageSize);
  }, [brandWeights, pageIndex, pageSize]);

  const dataTableColumns = useMemo(() => {
    const cols: TableColumn<any>[] = [
      {
        key: 'brand',
        header: 'Brand',
        accessor: (row: any) => row.brand?.name,
        render: (_v: any, row: any) => <div className="font-medium">{row.brand?.name}</div>,
      },
      {
        key: 'total_wt',
        header: 'Total Wt.',
        accessor: (row: any) => totalWeight(row),
        render: (_v: any, row: any) => {
          const total = totalWeight(row);
          return (
            <span
              className={`font-bold px-2 py-1 rounded ${
                total !== 1
                  ? 'text-red-600 bg-red-100'
                  : 'text-green-600 bg-green-100'
              }`}
            >
              {total.toFixed(2)}
            </span>
          );
        },
      },
      {
        key: 'revenue',
        header: 'Revenue Wt.',
        accessor: 'revenue',
      },
      {
        key: 'aov',
        header: 'AOV Wt.',
        accessor: 'aov',
      },
      {
        key: 'click',
        header: 'Clicks Wt.',
        accessor: 'click',
      },
      {
        key: 'conv_rate',
        header: 'Conv. Rate Wt.',
        accessor: 'conv_rate',
      },
      {
        key: 'new_v_existing',
        header: 'New v/s Exist. Wt.',
        accessor: 'new_v_existing',
      },
      {
        key: 'ecpc',
        header: 'eCPC Wt.',
        accessor: 'ecpc',
      },
      {
        key: 'conversions',
        header: 'Conversions Wt.',
        accessor: 'conversions',
      },
      {
        key: 'new_user',
        header: 'New User % Wt.',
        accessor: 'new_user',
      },
    ];
    if (hasEditAccess) {
      cols.push({
        key: 'actions',
        header: 'Actions',
        accessor: (row: any) => row.brand_id ?? row.id,
        width: '100px',
        align: 'center' as const,
        render: (_v: any, row: any) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editBrandWeight(row)}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
        ),
      });
    }
    return cols;
  }, [hasEditAccess]);

  return (
    <div className="flex flex-col h-full bg-white">
      <PageHeader
        title={`Brand Weights ${!searchText.trim() ? `(${brandWeights.length})` : `(${brandWeights.length} of ${brandWeightsBackup.length})`}`}
        renderCustomTop={
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => throttleGetBrandWeights()}
              className="flex items-center gap-2 h-8 px-3"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            {admin_data && (
              <Button
                asChild
                variant="outline"
                size="sm"
                className="flex items-center gap-2 h-8 px-3"
              >
                <a
                  rel="noreferrer"
                  href={`https://app.lift.lt.partners/testload?_id=${admin_data.user}&token=${admin_data?.token}&onboard=${1}`}
                  target="_blank"
                >
                  Go to LIFT
                </a>
              </Button>
            )}
            <div className="relative ml-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search brand weights..."
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  searchOptimized(e.target.value);
                }}
                className="pl-10 w-80"
              />
            </div>
          </div>
        }
      />

      <div className="flex-1 flex flex-col min-h-0 px-8 pt-8 pb-4">
        <div className="flex-1 min-h-0">
          <DataTable
            className="flex-1 min-h-0 overflow-auto"
            maxHeight="calc(100vh - 170px)"
            columns={dataTableColumns}
            data={paginatedData}
            loading={listLoad}
            emptyMessage={searchText ? 'No brand weights found matching your search.' : 'No brand weights found.'}
            stickyHeader
            stickyFooter
            showActions={false}
            pagination={{
              currentPage: pageIndex + 1,
              totalPages: Math.max(1, Math.ceil(brandWeights.length / pageSize)),
              pageSize,
              totalItems: brandWeights.length,
              onPageChange: (p) => setPageIndex(p - 1),
              onPageSizeChange: (size) => { setPageSize(size); setPageIndex(0); },
              pageSizeOptions: [25, 50, 100, 200],
            }}
          />
        </div>
      </div>

      {/* Edit Brand Weight Sheet */}
      <Sheet
        open={showEditBrandWeightDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowEditBrandWeightDialog(false);
          }
        }}
      >
        <SheetContent
          side="right"
          className="w-[600px] sm:max-w-[600px] flex flex-col h-full overflow-y-auto"
        >
          <SheetHeader>
            <SheetTitle>
              Edit Brand Weights - <span className="font-bold">{editBrand}</span>
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-4 p-4">
              <p className="text-sm text-gray-600">
                All weights should be decimal. Weights range is <b>0.0 to 1.0</b>
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="revenue">Revenue Wt. *</Label>
                  <Input
                    id="revenue"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editRevenue || ''}
                    onChange={(e) => setEditRevenue(e.target.value)}
                  />
                        </div>

                <div className="space-y-2">
                  <Label htmlFor="aov">AOV Wt. *</Label>
                  <Input
                    id="aov"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editAOV || ''}
                    onChange={(e) => setEditAOV(e.target.value)}
                  />
                        </div>

                <div className="space-y-2">
                  <Label htmlFor="clicks">Clicks Wt. *</Label>
                  <Input
                    id="clicks"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editClicks || ''}
                    onChange={(e) => setEditClicks(e.target.value)}
                  />
                        </div>

                <div className="space-y-2">
                  <Label htmlFor="conv_rate">Conversion Rate Wt. *</Label>
                  <Input
                    id="conv_rate"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editConvRate || ''}
                    onChange={(e) => setEditConvRate(e.target.value)}
                  />
                        </div>

                <div className="space-y-2">
                  <Label htmlFor="new_v_existing">New v/s Existing Wt. *</Label>
                  <Input
                    id="new_v_existing"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editNewVExisting || ''}
                    onChange={(e) => setEditNewVExisting(e.target.value)}
                  />
                        </div>

                <div className="space-y-2">
                  <Label htmlFor="ecpc">eCPC Wt. *</Label>
                  <Input
                    id="ecpc"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editECPC || ''}
                    onChange={(e) => setEditECPC(e.target.value)}
                  />
                        </div>

                <div className="space-y-2">
                  <Label htmlFor="conversions">Conversions Wt. *</Label>
                  <Input
                    id="conversions"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editConversions || ''}
                    onChange={(e) => setEditConversions(e.target.value)}
                  />
                        </div>

                <div className="space-y-2">
                  <Label htmlFor="new_user">New User % Wt. *</Label>
                  <Input
                    id="new_user"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editNewUser || ''}
                    onChange={(e) => setEditNewUser(e.target.value)}
                  />
                        </div>
                        </div>
                        </div>
                        </div>
          <SheetFooter className="border-t pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowEditBrandWeightDialog(false)}
            >
              Cancel
            </Button>
            {hasEditAccess && (
              <Button
                onClick={updateBrandWeight}
                disabled={!hideEditBrandWeightProgressDialog}
              >
                {hideEditBrandWeightProgressDialog ? 'Update' : <SpinnerLoader size={16} />}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default BrandWeights;
